/** @jsxImportSource @emotion/react */
import isequal from 'lodash.isequal';
import omit from 'lodash.omit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { filtersPanelAnalytics } from '../../analyticsConsts';
import {
  ALL_FA_PROGRAMS,
  ALL_WAYS_TO_PLAY,
  CLUB,
  CLUB_CASUAL,
  KICKABOUT_CASUAL,
  MAP_PAGE,
  SELECTED_RECOMMENDATIONS_TYPE_ID,
} from '../../const';
import { usePreferencesContext } from '../../context/preferences';
import { useRecommendations } from '../../context/recommendations';
import useErrorBoundary from '../../hooks/useErrorBoundary';
import useStorageFilters from '../../hooks/useStorageFilters';
import { getRecommendations } from '../../services';
import objectsDiff from '../../utils/objectsDiff';
import Button from '../Button';
import CFAContactCountyBNR from '../CFAContactCountyBNR';
import FilterByAge from '../FilterByAge';
import FilterByDays from '../FilterByDays';
import FilterByDisability from '../FilterByDisability';
import FilterByDisabilityOptions from '../FilterByDisabilityOptions';
import FilterByDistance from '../FilterByDistance';
import FilterByFootballType from '../FilterByFootballType';
import FilterByGender from '../FilterByGender';
import FilterByLocation from '../FilterByLocation';
import FilterByPrograms from '../FilterByPrograms';
import FilterByWaysToPlay from '../FilterByWaysToPlay';
import Loader from '../Loader';
import useStyles from './styles';

const { FILTERS_PANEL_CTA_APPLY, FILTERS_PANEL_CTA_CLEAR } =
  filtersPanelAnalytics;

const FiltersMenu = ({
  filterShowHide,
  page,
  onLoadingChange,
  setFilterShowHide,
  onResetVisibleCount
}) => {
  const styles = useStyles();
  const history = useHistory();
  const throwError = useErrorBoundary();
  const {
    clearFiltersAppliedStorage,
    currentFiltersParsed,
    updateFiltersStorage,
  } = useStorageFilters();
  const currentFiltersKeys = Object.keys(currentFiltersParsed);
  const currentFiltersLength = currentFiltersKeys.length;
  const [isLoading, setIsLoading] = useState(false);
  const { preferences, setPreferences } = usePreferencesContext();
  const [unsavedPreferences, setUnsavedPreferences] = useState({
    ...preferences,
    RawWeekDays:
      preferences.RawWeekDays ||
      (preferences.WeekDays ? preferences.WeekDays.split(',') : []),
  });
  const { setRecommendations } = useRecommendations();
  const { SelectedFootballType, quickSearch } = preferences;

  const [ageHasError, setAgeHasError] = useState(false);
  const [locationHasError, setLocationHasError] = useState(false);
  const [currentGeoLocation, setCurrentGeoLocation] = useState(false);
  const [disabilityOptionHasError, setDisabilityOptionHasError] =
    useState(false);
  const [isLocationReadOnly, setIsLocationReadOnly] = useState(false);
  const [distanceChanged, setDistanceChanged] = useState(false);

  useEffect(() => {
    if (quickSearch) {
      history.replace({
        pathname: history.location.pathname,
        search: `?Location=${preferences.Latitude},${preferences.Longitude}&Distance=${preferences.Distance}`,
      });
    }
  }, [quickSearch, preferences.Latitude, preferences.Longitude, preferences.Distance, history]);

  //Keep the location input readonly when its coming from location search.
  useEffect(() => {
    if (unsavedPreferences?.ReadableLocation) {
      setIsLocationReadOnly(true);
    } else {
      setIsLocationReadOnly(false);
    }
  }, [unsavedPreferences?.ReadableLocation]);

  useEffect(() => {
    if (filterShowHide && page === 'map') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [filterShowHide, page]);

  const handleAgeError = useCallback(hasError => {
    setAgeHasError(hasError);
  }, []);

  const handleDisabilityOptionError = useCallback(hasError => {
    setDisabilityOptionHasError(hasError);
  }, []);

  const handleLocationError = useCallback(hasError => {
    setLocationHasError(hasError);
  }, []);

  const handleManualInput = useCallback(() => {
    setCurrentGeoLocation(false);
  }, []);

  const keysToOmit = useMemo(() => {
    const keys = ['SelectedRecommendationsType'];
    if (
      unsavedPreferences.Disabilityoption === 2 ||
      unsavedPreferences.Disabilityoption === 3
    ) {
      keys.push('DisabilityType');
    }
    return keys;
  }, [unsavedPreferences.Disabilityoption]);

  const normalizePreferences = pref => ({
    ...pref,
    Age: pref.Age === '' ? 0 : pref.Age,
    RawWeekDays:
      pref.RawWeekDays || (pref.WeekDays ? pref.WeekDays.split(',') : []),
  });

  const unsavedChanges = useMemo(() => {
    return !isequal(
      omit(normalizePreferences(preferences), keysToOmit),
      omit(normalizePreferences(unsavedPreferences), keysToOmit)
    );
  }, [preferences, unsavedPreferences, keysToOmit]);

  const setLoadingState = useCallback(
    loading => {
      setIsLoading(loading);
      if (onLoadingChange) {
        onLoadingChange(loading);
      }
    },
    [onLoadingChange]
  );

  const onFilterSave = useCallback(
    updatedPreference => {
      setUnsavedPreferences(prev => ({
        ...prev,
        ...updatedPreference,
        [SELECTED_RECOMMENDATIONS_TYPE_ID]:
          preferences[SELECTED_RECOMMENDATIONS_TYPE_ID],
      }));
    },
    [preferences]
  );

  const onFilterSaveAll = useCallback(
    (shouldUpdateStorage = true) => {
      setLoadingState(true);
      setFilterShowHide(false);
      const baseUnsavedObj = {
        ...unsavedPreferences,
        ...(unsavedPreferences.Disabilityoption !== 1 && {
          DisabilityType: [],
        }),
      };

      if (!distanceChanged) {
        if (baseUnsavedObj.Disabilityoption === 1) {
          baseUnsavedObj.Distance = 200;
        }
      }
      // Omit fields when FootballType is 2 (CLUB)
      const unsavedObj =
        baseUnsavedObj?.FootballType === CLUB
          ? omit(baseUnsavedObj, ['Programs', 'RawPrograms'])
          : baseUnsavedObj;

      setCurrentGeoLocation(false);

      const backendObj = omit(unsavedObj, ['RawWeekDays']);

      getRecommendations(SelectedFootballType, backendObj)
        .then(data => {
          if (data) {
            const recommendationRecords = data.data;
            const backupData = recommendationRecords;
            if (
              page === MAP_PAGE &&
              recommendationRecords.RecommendationClubCentreCartDto.length > 0
            ) {
              // code will be written here for map page
            }
            setPreferences(unsavedObj);
            setUnsavedPreferences(unsavedObj);
            setDistanceChanged(false);
            onResetVisibleCount();
            setRecommendations(
              page === MAP_PAGE ? backupData : recommendationRecords,
              SelectedFootballType
            );
            const diffPreferences = objectsDiff(unsavedObj, preferences);
            if (shouldUpdateStorage) {
              updateFiltersStorage(diffPreferences);
            } else {
              setFilterShowHide(true);
            }
            setLoadingState(false);
          }
        })
        .catch(throwError);
    },
    [
      SelectedFootballType,
      setLoadingState,
      setFilterShowHide,
      unsavedPreferences,
      page,
      setPreferences,
      setRecommendations,
      preferences,
      updateFiltersStorage,
      throwError,
      distanceChanged,
      onResetVisibleCount
    ],
  );

  // for clear filters
  const onClearFilters = useCallback(() => {
    const clearedAllPreferences = {
      ...unsavedPreferences,
      Age: 0,
      PlayWith: 7,
      FootballType: 4,
      FootballTypeAllNone: 'None',
      DisabilityType: [],
      Disabilityoption: 3,
      WeekDays: '1,2,3,4,5,6,7',
      RawWeekDays: [],
      Programs: ALL_FA_PROGRAMS,
      RawPrograms: [],
      WaysToPlay: ALL_WAYS_TO_PLAY,
      RawWaysToPlay: [],
    };
    // Reset all filters except location
    setUnsavedPreferences(clearedAllPreferences);
    // Reset any related error states
    setDisabilityOptionHasError(false);
  }, [unsavedPreferences]);

  const [triggerSave, setTriggerSave] = useState(false);

  useEffect(() => {
    if (triggerSave) {
      onFilterSaveAll(false);
      clearFiltersAppliedStorage();

      setTriggerSave(false);
    }
  }, [
    triggerSave,
    unsavedPreferences,
    onFilterSaveAll,
    clearFiltersAppliedStorage,
  ]);

  return (
    <>
      {filterShowHide && (
        <>
          <div css={styles.overlay(filterShowHide, page)} />
          <aside css={styles.aside(filterShowHide)}>
            <div css={styles.asidebar}>
              <div css={styles.filters(page)}>
                <div css={styles.filter}>
                  <FilterByLocation
                    onSave={onFilterSave}
                    currentValue={unsavedPreferences.ReadableLocation}
                    onErrorChange={handleLocationError}
                    locationHasError={locationHasError}
                    currentGeoLocation={currentGeoLocation}
                    setCurrentGeoLocation={setCurrentGeoLocation}
                    onManualInput={handleManualInput}
                    preferences={preferences}
                    filterShowHide={filterShowHide}
                    readOnly={isLocationReadOnly}
                    onUnlockLocation={() => setIsLocationReadOnly(false)}
                    magnifierIconVariant="dark"
                    LoaderComponent={Loader}
                  />
                </div>

                <div css={styles.filter}>
                  <FilterByDistance
                    onSave={onFilterSave}
                    currentValue={unsavedPreferences.Distance}
                    filterShowHide={filterShowHide}
                    disabilityOption={unsavedPreferences.Disabilityoption}
                    onDistanceChange={() => setDistanceChanged(true)}
                  />
                </div>

                <div css={styles.filter}>
                  <FilterByAge
                    onSave={onFilterSave}
                    currentValue={unsavedPreferences.Age}
                    currentSchoolYearValue={unsavedPreferences.SchoolYear}
                    onErrorChange={handleAgeError}
                    filterShowHide={filterShowHide}
                  />
                </div>

                <div css={styles.filter}>
                  <FilterByGender
                    onSave={onFilterSave}
                    currentValue={unsavedPreferences.PlayWith}
                    filterShowHide={filterShowHide}
                  />
                </div>

                <div css={styles.filter}>
                  <FilterByFootballType
                    onSave={onFilterSave}
                    currentValue={unsavedPreferences['FootballType']}
                    onErrorChange={''}
                    FootballTypeAllNone={
                      unsavedPreferences['FootballTypeAllNone']
                    }
                    filterShowHide={filterShowHide}
                  />
                </div>

                <div css={styles.filter}>
                  <FilterByDisability
                    onSave={onFilterSave}
                    currentValue={unsavedPreferences.Disabilityoption}
                    filterShowHide={filterShowHide}
                  />
                </div>
                {unsavedPreferences.Disabilityoption === 1 && (
                  <div css={styles.filter}>
                    <FilterByDisabilityOptions
                      onSave={onFilterSave}
                      currentValue={unsavedPreferences.DisabilityType}
                      onErrorChange={handleDisabilityOptionError}
                      disabilityOption={unsavedPreferences.Disabilityoption}
                      filterShowHide={filterShowHide}
                    />
                  </div>
                )}

                <div css={styles.filter}>
                  <FilterByDays
                    onSave={onFilterSave}
                    currentValue={(unsavedPreferences.RawWeekDays || []).join(
                      ','
                    )}
                    onErrorChange={''}
                    filterShowHide={filterShowHide}
                    quickSearch={quickSearch}
                  />
                </div>
                {(unsavedPreferences?.FootballType === KICKABOUT_CASUAL ||
                  unsavedPreferences?.FootballType === CLUB_CASUAL) && (
                    <div css={styles.filter}>
                      <FilterByPrograms
                        onSave={onFilterSave}
                        currentValue={(unsavedPreferences.RawPrograms || []).join(
                          ','
                        )}
                        onErrorChange={''}
                        filterShowHide={filterShowHide}
                      />
                    </div>
                  )}

                <div css={styles.filter}>
                  <FilterByWaysToPlay
                    onSave={onFilterSave}
                    currentValue={(unsavedPreferences.RawWaysToPlay || []).join(
                      ','
                    )}
                    onErrorChange={''}
                    filterShowHide={filterShowHide}
                  />
                </div>
                <CFAContactCountyBNR isFilter={true} />
              </div>
            </div>
            <div css={styles.filterButtons}>
              <Button
                primary
                id={FILTERS_PANEL_CTA_APPLY}
                css={styles.saveBtn}
                onClick={onFilterSaveAll}
                disabled={
                  (!currentGeoLocation &&
                    !unsavedPreferences.ReadableLocation) ||
                  !unsavedChanges ||
                  isLoading ||
                  ageHasError ||
                  locationHasError ||
                  disabilityOptionHasError
                }
              >
                {isLoading ? 'APPLYING FILTERS...' : `SHOW RESULTS`}
              </Button>

              <Button
                id={FILTERS_PANEL_CTA_CLEAR}
                css={styles.clearBtn}
                onClick={() => {
                  onClearFilters();
                  setTriggerSave(true);
                }}
                disabled={currentFiltersLength > 0 ? false : true}
              >
                CLEAR ALL
              </Button>
            </div>
          </aside>
        </>
      )}
    </>
  );
};

export default FiltersMenu;
